import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay } from "date-fns";
import { MagnifyingGlass, XCircle, TrendUp, TrendDown, ChartLine, CaretLeft, CaretRight, CalendarBlank, Check, X, Note, ArrowsClockwise, Funnel, Warning, Shield, Lock, Sun, Moon, Lightning, PencilSimple } from "phosphor-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SyncedScrollTable } from "@/components/ui/synced-scroll-table";
import GeniLoader from "../components/GeniLoader";
import TradeTypeBadge from "../components/TradeTypeBadge";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";

// Footer branding
const LOGO_PUREBRAIN = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/gtg4vc6a_purebrain.png";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function History() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Shared/Public profile mode - when viewing another user's profile
  const { isSharedProfileView, isReadOnly } = useSharedProfile();
  
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelId, setCancelId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reopenId, setReopenId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [highlightedTradeId, setHighlightedTradeId] = useState(null);
  
  // Trade type filter from navigation state or URL params
  const tradeTypeFromUrl = searchParams.get('trade_type');
  const [tradeTypeFilter, setTradeTypeFilter] = useState(location.state?.filter?.tradeType || tradeTypeFromUrl || null);
  
  // Asset Group filter from Advanced Analytics drilldown
  const assetGroupIdFromUrl = searchParams.get('asset_group_id');
  const assetGroupNameFromState = location.state?.assetGroupName || null;
  const [assetGroupFilter, setAssetGroupFilter] = useState(assetGroupIdFromUrl || null);
  const [assetGroupName, setAssetGroupName] = useState(assetGroupNameFromState || null);
  
  // Setup Group filter from Analytics drilldown (public profile)
  // Get setup_group_id and setup_ids from URL params, group name from location state
  const setupGroupId = searchParams.get('setup_group_id');
  const setupGroupSetupIds = searchParams.get('setup_group_setup_ids')?.split(',').filter(Boolean) || [];
  const setupGroupName = location.state?.setupGroupName || null;
  
  // Time period filter from Analytics drilldown
  const periodFromUrl = searchParams.get('period');
  const startDateFromUrl = searchParams.get('start_date');
  const endDateFromUrl = searchParams.get('end_date');
  const analyticsScope = searchParams.get('analytics_scope');
  const monthlyPerformanceLabel = location.state?.monthlyPerformanceLabel || null;
  
  // Direction filter from Analytics drilldown (long / short)
  const directionFromUrl = searchParams.get('direction');
  
  // Special view mode from query params (e.g., recent-rr insight)
  const viewMode = searchParams.get('view');
  
  // RR subset view state: 'recent' (default) or 'previous' for the recent-rr view mode
  const [rrSubsetView, setRrSubsetView] = useState('recent');
  
  // Winning RR subset view state: 'recent' (default) or 'previous' for the winning-rr view mode
  const [winningRrSubsetView, setWinningRrSubsetView] = useState('recent');
  
  // Get condition type from URL params (for winning-rr view)
  const conditionType = searchParams.get('condition');
  
  const highlightedRef = useRef(null);
  
  // Notes drawer state (same pattern as OpenPositions)
  const [notesDrawerTrade, setNotesDrawerTrade] = useState(null);
  const [drawerNote, setDrawerNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // Trade type edit permission (special accounts only)
  const [canEditClosedTradeType, setCanEditClosedTradeType] = useState(false);
  const [updatingTradeTypeId, setUpdatingTradeTypeId] = useState(null);
  
  // Legacy stop correction (special accounts only - for historical trade repair)
  const [editingLegacyStop, setEditingLegacyStop] = useState(null); // trade.id being edited
  const [legacyStopValue, setLegacyStopValue] = useState("");
  const [savingLegacyStop, setSavingLegacyStop] = useState(false);
  
  // Calendar filter state
  const [calendarFilter, setCalendarFilter] = useState(null); // { type: 'day'|'week', date: Date, weekDays?: Date[] }
  
  // Frontend simple period filter (Todos | 7D | 30D)
  const [timeFilter, setTimeFilter] = useState('Todos');
  
  // Capital data for P&L percentage calculations
  const [initialCapital, setInitialCapital] = useState(0);
  const [adjustments, setAdjustments] = useState([]);

  const getTradeTimelineDate = (trade) => {
    const rawValue = trade.closed_at || trade.created_at || trade.updated_at;
    if (!rawValue) return null;

    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isEligibleAnalyticsTrade = (trade) => {
    const outcome = String(trade.outcome || '').toLowerCase();
    return trade.status === 'closed' && ['win', 'loss', 'breakeven'].includes(outcome);
  };

  // Reopen Trade: closed trades can be reopened within 12h of their real closing timestamp
  const REOPEN_WINDOW_HOURS = 12;
  const isReopenEligible = (trade) => {
    if (trade.status !== 'closed' || !trade.closed_at) return false;
    const closedAt = new Date(trade.closed_at);
    if (Number.isNaN(closedAt.getTime())) return false;
    const hoursSinceClose = (Date.now() - closedAt.getTime()) / (1000 * 60 * 60);
    return hoursSinceClose <= REOPEN_WINDOW_HOURS;
  };

  // Cancel reason options
  const cancelReasonOptions = [
    { value: 'setup_not_triggered', label: 'Setup not triggered' },
    { value: 'no_entry_reach', label: 'Did not reach entry point' },
    { value: 'skipped_execution', label: 'Skipped execution (no clear reason)' }
  ];

  // Get human readable cancel reason
  const getCancelReasonLabel = (reason) => {
    const option = cancelReasonOptions.find(o => o.value === reason);
    return option ? option.label : 'Skipped execution';
  };

  // Cleanup on unmount to prevent navigation bugs
  useEffect(() => {
    return () => {
      setNotesDrawerTrade(null);
      setSelectedTrade(null);
    };
  }, []);

  useEffect(() => {
    fetchTrades();
  }, []);

  // Handle highlight parameter from URL
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && trades.length > 0) {
      setHighlightedTradeId(highlightId);
      // Find and select the trade
      const trade = trades.find(t => t.id === highlightId);
      if (trade) {
        setSelectedTrade(trade);
      }
      // Clear the URL parameter
      setSearchParams({});
      // Remove highlight after 5 seconds
      setTimeout(() => {
        setHighlightedTradeId(null);
      }, 5000);
    }
  }, [searchParams, trades, setSearchParams]);

  // Update trade type filter when URL param changes
  useEffect(() => {
    const tradeTypeParam = searchParams.get('trade_type');
    if (tradeTypeParam && tradeTypeParam !== tradeTypeFilter) {
      setTradeTypeFilter(tradeTypeParam);
    }
  }, [searchParams]);

  // Scroll to highlighted trade
  useEffect(() => {
    if (highlightedTradeId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedTradeId]);

  const fetchTrades = async () => {
    try {
      const response = await api.get('/trades');
      setTrades(response.data);
    } catch (error) {
      console.error("Error fetching trades:", error);
      toast.error("Failed to load trade history");
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial capital and adjustments for percentage calculations
  // Balance adjustments is a private endpoint - only fetch if not in shared profile mode
  const fetchCapitalData = async () => {
    try {
      const settingsRes = await api.get('/settings');
      setInitialCapital(settingsRes.data.initial_capital || 0);
      
      // Only fetch balance adjustments if NOT in shared profile mode
      if (!isSharedProfileView) {
        try {
          const adjustmentsRes = await api.get('/balance-adjustments');
          setAdjustments(adjustmentsRes.data || []);
        } catch (adjError) {
          console.log("Could not fetch balance adjustments (may be private):", adjError);
          setAdjustments([]);
        }
      } else {
        setAdjustments([]);
      }
    } catch (error) {
      console.error("Error fetching capital data:", error);
      setAdjustments([]);
    }
  };

  // Calculate capital at start of a given day (historical snapshot)
  // This includes: initial capital + adjustments before that day + realized P&L before that day
  const getCapitalAtStartOfDay = useMemo(() => {
    return (targetDay) => {
      if (!initialCapital) return null;
      
      const targetDate = new Date(targetDay);
      targetDate.setHours(0, 0, 0, 0);
      
      // Sum adjustments before the target day
      const adjustmentsBefore = adjustments.reduce((sum, adj) => {
        const adjDate = new Date(adj.date || adj.created_at);
        adjDate.setHours(0, 0, 0, 0);
        return adjDate < targetDate ? sum + (adj.amount || 0) : sum;
      }, 0);
      
      // Sum realized P&L from closed trades before the target day
      const closedTrades = trades.filter(t => t.status === 'closed' && t.profit_usd !== null);
      const pnlBefore = closedTrades.reduce((sum, trade) => {
        const closeDate = new Date(trade.updated_at || trade.created_at);
        closeDate.setHours(0, 0, 0, 0);
        return closeDate < targetDate ? sum + (trade.profit_usd || 0) : sum;
      }, 0);
      
      return initialCapital + adjustmentsBefore + pnlBefore;
    };
  }, [initialCapital, adjustments, trades]);

  // Calculate capital at start of a given week
  const getCapitalAtStartOfWeek = useMemo(() => {
    return (weekStartDay) => {
      return getCapitalAtStartOfDay(weekStartDay);
    };
  }, [getCapitalAtStartOfDay]);

  // Load capital data on mount
  useEffect(() => {
    fetchCapitalData();
  }, []);

  // Fetch user permissions for special features
  useEffect(() => {
    const fetchPermissions = async () => {
      // Don't fetch in shared profile view
      if (isSharedProfileView) return;
      
      try {
        const response = await api.get('/user/permissions');
        setCanEditClosedTradeType(response.data?.can_edit_closed_trade_type || false);
      } catch (error) {
        console.error('Error fetching permissions:', error);
        setCanEditClosedTradeType(false);
      }
    };
    
    fetchPermissions();
  }, [isSharedProfileView]);

  // Update trade type for closed trades (authorized accounts only)
  const handleUpdateTradeType = async (tradeId, newTradeType) => {
    if (!canEditClosedTradeType) {
      toast.error('Not authorized to edit trade type');
      return;
    }
    
    setUpdatingTradeTypeId(tradeId);
    
    try {
      await api.patch(`/trades/${tradeId}/trade-type`, { trade_type: newTradeType });
      
      // Update local state
      setTrades(prevTrades => 
        prevTrades.map(t => 
          t.id === tradeId ? { ...t, trade_type: newTradeType } : t
        )
      );
      
      // Update selected trade if open
      if (selectedTrade?.id === tradeId) {
        setSelectedTrade(prev => ({ ...prev, trade_type: newTradeType }));
      }
      
      toast.success('Trade type updated');
    } catch (error) {
      console.error('Error updating trade type:', error);
      if (error.response?.status === 403) {
        toast.error('Not authorized to edit trade type on closed trades');
      } else {
        toast.error('Failed to update trade type');
      }
    } finally {
      setUpdatingTradeTypeId(null);
    }
  };

  // Legacy stop correction for historical trades (authorized accounts only)
  const handleLegacyStopCorrection = async (tradeId) => {
    if (!canEditClosedTradeType) {
      toast.error('Not authorized for legacy corrections');
      return;
    }
    
    const correctedStop = parseFloat(legacyStopValue);
    if (isNaN(correctedStop) || correctedStop <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }
    
    setSavingLegacyStop(true);
    
    try {
      const response = await api.patch(`/trades/${tradeId}/legacy-stop-correction`, { 
        corrected_stop: correctedStop 
      });
      
      const { new_stop, new_rr, new_profit_usd, new_outcome } = response.data;
      
      // Update local state with corrected values
      setTrades(prevTrades => 
        prevTrades.map(t => 
          t.id === tradeId ? { 
            ...t, 
            stop_loss: new_stop,
            rr: new_rr,
            profit_usd: new_profit_usd,
            outcome: new_outcome,
            legacy_stop_corrected: true
          } : t
        )
      );
      
      // Update selected trade if open
      if (selectedTrade?.id === tradeId) {
        setSelectedTrade(prev => ({ 
          ...prev, 
          stop_loss: new_stop,
          rr: new_rr,
          profit_usd: new_profit_usd,
          outcome: new_outcome,
          legacy_stop_corrected: true
        }));
      }
      
      toast.success(`Stop corrected: RR updated to ${new_rr > 0 ? '+' : ''}${new_rr}R`);
      setEditingLegacyStop(null);
      setLegacyStopValue("");
    } catch (error) {
      console.error('Error correcting legacy stop:', error);
      if (error.response?.status === 403) {
        toast.error('Not authorized for legacy corrections');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to correct stop');
      }
    } finally {
      setSavingLegacyStop(false);
    }
  };

  // Filter trades by trade type if filter is active, or by special view mode
  const filteredTrades = useMemo(() => {
    let result = trades;
    
    // Special view modes from insights
    if (viewMode === 'recent-rr') {
      // Get all closed trades sorted by date (same logic as Dashboard RR comparison)
      const closedTrades = trades
        .filter(t => t.status === 'closed' && (t.outcome === 'win' || t.outcome === 'loss'))
        .sort((a, b) => new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at));
      
      // Show recent 10 or previous 10 based on subset selection
      if (rrSubsetView === 'previous') {
        return closedTrades.slice(10, 20);
      }
      return closedTrades.slice(0, 10);
    }
    
    // Winning RR view mode - uses EXACT same logic as Dashboard warning
    if (viewMode === 'winning-rr') {
      // Filter: closed trades, outcome='win', hand_size > 0, profit_usd not null
      // Sort: by created_at descending (NOT closed_at - matches warning logic exactly)
      const winningTrades = trades
        .filter(t => 
          t.status === 'closed' && 
          t.outcome === 'win' &&
          t.hand_size > 0 &&
          t.profit_usd !== null &&
          t.profit_usd !== undefined
        )
        .sort((a, b) => (getTradeTimelineDate(b)?.getTime() || 0) - (getTradeTimelineDate(a)?.getTime() || 0));
      
      // For trend condition: show recent 5 or previous 5 based on subset selection
      // For static condition: show all winning trades
      if (conditionType === 'trend') {
        if (winningRrSubsetView === 'previous') {
          return winningTrades.slice(5, 10);
        }
        return winningTrades.slice(0, 5);
      } else {
        // Static condition - show all winning trades (or top 20 for readability)
        return winningTrades.slice(0, 20);
      }
    }
    
    // Losing streak view mode - shows trades from the losing streak days
    if (viewMode === 'losing-streak') {
      const streakDaysCount = parseInt(searchParams.get('days')) || 3;
      
      // Get closed trades with profit data (same logic as Dashboard calculateTradingStreak)
      const closedTrades = trades.filter(t => 
        t.profit_usd !== null && t.profit_usd !== undefined
      );
      
      // Group trades by day (YYYY-MM-DD)
      const tradesByDay = {};
      closedTrades.forEach(trade => {
        const timelineDate = getTradeTimelineDate(trade);
        if (!timelineDate) return;
        const dayKey = timelineDate.toISOString().split('T')[0];
        if (!tradesByDay[dayKey]) {
          tradesByDay[dayKey] = [];
        }
        tradesByDay[dayKey].push(trade);
      });
      
      // Calculate daily PnL and filter active days (non-zero PnL)
      const activeDays = Object.entries(tradesByDay)
        .map(([date, dayTrades]) => {
          const totalPnL = dayTrades.reduce((sum, t) => sum + (t.profit_usd || 0), 0);
          return { date, totalPnL, trades: dayTrades };
        })
        .filter(day => day.totalPnL !== 0)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Find the losing streak days from most recent
      const losingStreakDays = [];
      for (const day of activeDays) {
        if (day.totalPnL < 0) {
          losingStreakDays.push(day);
          if (losingStreakDays.length >= streakDaysCount) break;
        } else {
          break; // Streak ended
        }
      }
      
      // Get all trades from the losing streak days
      const streakTrades = losingStreakDays.flatMap(day => day.trades)
        .sort((a, b) => (getTradeTimelineDate(b)?.getTime() || 0) - (getTradeTimelineDate(a)?.getTime() || 0));
      
      return streakTrades;
    }
    
    if (analyticsScope === 'eligible_closed') {
      result = result.filter(isEligibleAnalyticsTrade);
    }

    // Setup Group filter (from Analytics drilldown in public profile)
    if (setupGroupId && setupGroupSetupIds.length > 0) {
      result = result.filter(trade => {
        // Trade's setup_id must be in the list of setup IDs that belong to this group
        return trade.setup_id && setupGroupSetupIds.includes(trade.setup_id);
      });
    }

    // Apply time period filter if provided from Analytics / Monthly Performance drilldown
    if (periodFromUrl) {
      const now = new Date();
      let startDate;

      switch (periodFromUrl) {
        case '7':
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30':
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90':
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        result = result.filter(trade => {
          const tradeDate = getTradeTimelineDate(trade);
          return tradeDate ? tradeDate >= startDate : false;
        });
      }
    } else if (startDateFromUrl && endDateFromUrl) {
      const startDate = new Date(startDateFromUrl);
      const endDate = new Date(endDateFromUrl);

      result = result.filter(trade => {
        const tradeDate = getTradeTimelineDate(trade);
        return tradeDate ? tradeDate >= startDate && tradeDate <= endDate : false;
      });
    }

    // Apply direction filter if provided from Analytics
    if (directionFromUrl && directionFromUrl !== 'all') {
      result = result.filter(trade => {
        return trade.direction && trade.direction.toLowerCase() === directionFromUrl.toLowerCase();
      });
    }
    
    // Trade type filter
    if (tradeTypeFilter) {
      result = result.filter(trade => trade.trade_type === tradeTypeFilter);
    }
    
    // Asset Group filter (from Advanced Analytics drilldown)
    if (assetGroupFilter) {
      result = result.filter(trade => trade.asset_group_id === assetGroupFilter);
    }
    
    // Calendar filter (day or week)
    if (calendarFilter) {
      if (calendarFilter.type === 'day') {
        const dayStr = format(calendarFilter.date, 'yyyy-MM-dd');
        result = result.filter(trade => {
          const tradeDate = getTradeTimelineDate(trade);
          if (!tradeDate) return false;
          return format(tradeDate, 'yyyy-MM-dd') === dayStr;
        });
      } else if (calendarFilter.type === 'week' && calendarFilter.weekDays) {
        const weekDayStrs = calendarFilter.weekDays.map(d => format(d, 'yyyy-MM-dd'));
        result = result.filter(trade => {
          const tradeDate = getTradeTimelineDate(trade);
          if (!tradeDate) return false;
          return weekDayStrs.includes(format(tradeDate, 'yyyy-MM-dd'));
        });
      }
    }
    
    // Apply frontend time period filter (Todos | 7D | 30D)
    if (timeFilter !== 'Todos') {
      const now = new Date();
      let limitDate;
      if (timeFilter === '7D') {
        limitDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (timeFilter === '30D') {
        limitDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      if (limitDate) {
        result = result.filter(trade => {
          const tradeDate = getTradeTimelineDate(trade);
          if (!tradeDate) return false;
          return tradeDate >= limitDate;
        });
      }
    }
    
    return result;
  }, [trades, tradeTypeFilter, assetGroupFilter, viewMode, rrSubsetView, winningRrSubsetView, conditionType, calendarFilter, setupGroupId, setupGroupSetupIds, periodFromUrl, startDateFromUrl, endDateFromUrl, directionFromUrl, analyticsScope, timeFilter]);

  // Clear trade selection if the currently selected trade falls outside the applied time filter
  useEffect(() => {
    if (selectedTrade) {
      const isStillVisible = filteredTrades.some(t => t.id === selectedTrade.id);
      if (!isStillVisible) {
        setSelectedTrade(null);
      }
    }
  }, [timeFilter, filteredTrades, selectedTrade]);

  // Clear filter and reset navigation state
  const clearTradeTypeFilter = () => {
    setTradeTypeFilter(null);
    // Clear navigation state
    navigate('/history', { replace: true, state: {} });
  };
  
  // Clear Setup Group filter (keep same route but remove query params)
  const clearSetupGroupFilter = () => {
    // Get current path without query params
    const currentPath = location.pathname;
    navigate(currentPath, { replace: true, state: {} });
  };

  const clearAnalyticsDrilldownFilter = () => {
    const currentPath = location.pathname;
    navigate(currentPath, { replace: true, state: {} });
  };

  // Clear Asset Group filter
  const clearAssetGroupFilter = () => {
    setAssetGroupFilter(null);
    setAssetGroupName(null);
    // Remove asset_group_id from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('asset_group_id');
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true, state: {} });
  };

  // Clear view mode from URL
  const clearViewMode = () => {
    setSearchParams({});
  };

  // Calendar click handlers
  const handleDayClick = (day) => {
    // Check if there are any trades on this day
    const dayStr = format(day, 'yyyy-MM-dd');
    const hasTrades = trades.some(trade => {
      const tradeDate = getTradeTimelineDate(trade);
      if (!tradeDate) return false;
      return format(tradeDate, 'yyyy-MM-dd') === dayStr;
    });
    
    // Toggle off if clicking the same day
    if (calendarFilter?.type === 'day' && isSameDay(calendarFilter.date, day)) {
      setCalendarFilter(null);
      return;
    }
    
    // Set day filter (even if no trades, to show empty state)
    setCalendarFilter({ type: 'day', date: day });
  };

  const handleWeekClick = (weekDays) => {
    // Check if clicking the same week
    if (calendarFilter?.type === 'week' && calendarFilter.weekDays) {
      const currentWeekStart = format(calendarFilter.weekDays[0], 'yyyy-MM-dd');
      const newWeekStart = format(weekDays[0], 'yyyy-MM-dd');
      if (currentWeekStart === newWeekStart) {
        setCalendarFilter(null);
        return;
      }
    }
    
    setCalendarFilter({ type: 'week', date: weekDays[0], weekDays });
  };

  const clearCalendarFilter = () => {
    setCalendarFilter(null);
  };

  // Format calendar filter label
  const getCalendarFilterLabel = () => {
    if (!calendarFilter) return null;
    
    if (calendarFilter.type === 'day') {
      return `Showing trades for ${format(calendarFilter.date, 'MMMM d, yyyy')}`;
    }
    
    if (calendarFilter.type === 'week' && calendarFilter.weekDays) {
      const weekStart = calendarFilter.weekDays[0];
      const weekEnd = calendarFilter.weekDays[6];
      return `Showing trades for week of ${format(weekStart, 'MMM d')}–${format(weekEnd, 'MMM d, yyyy')}`;
    }
    
    return null;
  };

  // Calculate RR comparison data for the recent-rr view mode
  const rrComparisonData = useMemo(() => {
    if (viewMode !== 'recent-rr') return null;
    
    // Get all closed trades sorted by date (same logic as Dashboard)
    const closedTrades = trades
      .filter(t => t.status === 'closed' && (t.outcome === 'win' || t.outcome === 'loss'))
      .sort((a, b) => new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at));
    
    // Need at least 20 trades for comparison
    if (closedTrades.length < 20) {
      return { hasEnoughData: false };
    }
    
    const recentTrades = closedTrades.slice(0, 10);
    const previousTrades = closedTrades.slice(10, 20);
    
    // Calculate average RR (same logic as Dashboard calcAvgRR)
    const calcAvgRR = (group) => {
      if (!group || group.length === 0) return 0;
      const withRR = group.filter(t => t.rr != null && !isNaN(t.rr));
      if (withRR.length === 0) return 0;
      return withRR.reduce((sum, t) => sum + t.rr, 0) / withRR.length;
    };
    
    const avgRR_recent = calcAvgRR(recentTrades);
    const avgRR_previous = calcAvgRR(previousTrades);
    const difference = avgRR_recent - avgRR_previous;
    const deltaPercent = Math.abs(avgRR_previous) > 0.1 
      ? ((avgRR_recent - avgRR_previous) / Math.abs(avgRR_previous)) * 100 
      : 0;
    
    return {
      hasEnoughData: true,
      recentAvgRR: avgRR_recent,
      previousAvgRR: avgRR_previous,
      difference,
      deltaPercent,
      isImproving: difference > 0,
      recentCount: recentTrades.filter(t => t.rr != null && !isNaN(t.rr)).length,
      previousCount: previousTrades.filter(t => t.rr != null && !isNaN(t.rr)).length
    };
  }, [trades, viewMode]);

  // Calculate Winning RR comparison data for the winning-rr view mode
  // Uses EXACT same logic as Dashboard warning: created_at sorting, profit_usd/hand_size calculation
  const winningRrComparisonData = useMemo(() => {
    if (viewMode !== 'winning-rr') return null;
    
    // Filter: EXACT same as Dashboard warning
    // closed trades, outcome='win', hand_size > 0, profit_usd not null
    const winningTrades = trades
      .filter(t => 
        t.status === 'closed' && 
        t.outcome === 'win' &&
        t.hand_size > 0 &&
        t.profit_usd !== null &&
        t.profit_usd !== undefined
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // Calculate average RR using EXACT same formula as warning: profit_usd / hand_size
    const calcWinRR = (group) => {
      if (!group || group.length === 0) return 0;
      return group.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / group.length;
    };
    
    const totalWinningCount = winningTrades.length;
    const avgWinRR = calcWinRR(winningTrades);
    
    // Trend comparison (recent 5 vs previous 5)
    let recentWinRR = null;
    let previousWinRR = null;
    let hasTrendData = false;
    let isTrendWarning = false;
    
    if (winningTrades.length >= 10) {
      const recent5Wins = winningTrades.slice(0, 5);
      const previous5Wins = winningTrades.slice(5, 10);
      
      recentWinRR = calcWinRR(recent5Wins);
      previousWinRR = calcWinRR(previous5Wins);
      hasTrendData = true;
      
      // Check if trend warning conditions are met (same as Dashboard)
      isTrendWarning = previousWinRR >= 1.0 && recentWinRR < previousWinRR && (previousWinRR - recentWinRR) >= 0.3;
    }
    
    // Static warning check (same as Dashboard)
    const isStaticWarning = avgWinRR < 1.2;
    
    return {
      hasEnoughData: winningTrades.length >= 5,
      totalWinningCount,
      avgWinRR,
      recentWinRR,
      previousWinRR,
      hasTrendData,
      isTrendWarning,
      isStaticWarning,
      difference: hasTrendData ? recentWinRR - previousWinRR : null
    };
  }, [trades, viewMode]);

  // Get view mode description
  const getViewModeDescription = () => {
    if (viewMode === 'recent-rr') {
      return {
        title: 'Recent RR Warning',
        description: 'Showing the 10 most recent closed trades used in the RR comparison insight.'
      };
    }
    if (viewMode === 'winning-rr') {
      if (conditionType === 'trend') {
        return {
          title: 'Winning RR Trend Warning',
          description: 'Your recent winning trades have lower RR than your previous winning trades.'
        };
      } else {
        return {
          title: 'Winning RR Warning',
          description: 'Your overall winning trade RR is below the optimal threshold (1.2R).'
        };
      }
    }
    if (viewMode === 'losing-streak') {
      const days = parseInt(searchParams.get('days')) || 3;
      return {
        title: `${days}-Day Losing Streak`,
        description: `Showing all trades from your ${days} consecutive losing days. Review patterns and consider slowing down.`
      };
    }
    return null;
  };

  // Get filter label for display
  const getFilterLabel = () => {
    if (tradeTypeFilter === 'scalp') return 'Scalp';
    if (tradeTypeFilter === 'day_trade') return 'Day Trade';
    if (tradeTypeFilter === 'swing_trade') return 'Swing Trade';
    return null;
  };

  const handleCancel = async (id, reason) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot cancel trades in view-only mode");
      return;
    }
    
    try {
      await api.post(`/trades/${id}/cancel?cancel_reason=${reason}`);
      toast.success("Trade marked as not triggered");
      setTrades(trades.map((trade) => 
        trade.id === id 
          ? { ...trade, status: 'canceled', outcome: 'canceled', cancel_reason: reason }
          : trade
      ));
      setCancelId(null);
      setCancelReason('');
      if (selectedTrade?.id === id) {
        setSelectedTrade(null);
      }
    } catch (error) {
      console.error("Error canceling trade:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel trade");
    }
  };

  const handleReopen = async (id) => {
    // Block in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot reopen trades in view-only mode");
      return;
    }

    setReopeningId(id);
    try {
      const response = await api.post(`/trades/${id}/reopen`);
      const reopenedTrade = response.data;
      toast.success("Trade reopened. It's back in Open Positions.");
      setTrades(trades.map((trade) => (trade.id === id ? { ...trade, ...reopenedTrade } : trade)));
      if (selectedTrade?.id === id) {
        setSelectedTrade({ ...selectedTrade, ...reopenedTrade });
      }
      setReopenId(null);
    } catch (error) {
      console.error("Error reopening trade:", error);
      toast.error(error.response?.data?.detail || "Failed to reopen trade");
    } finally {
      setReopeningId(null);
    }
  };

  // Calendar helper functions
  const getCalendarDays = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    // Week starts on Monday (1), so we use { weekStartsOn: 1 }
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    
    return days;
  };

  const goToPrevMonth = () => setCalendarMonth(subMonths(calendarMonth, 1));
  const goToNextMonth = () => setCalendarMonth(addMonths(calendarMonth, 1));
  const goToToday = () => setCalendarMonth(new Date());

  // Calculate daily PnL from closed trades (returns { usd, percent })
  const getDailyPnL = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const dayTrades = filteredTrades.filter(trade => {
      if (trade.status !== 'closed') return false;
      const tradeDate = getTradeTimelineDate(trade);
      if (!tradeDate) return false;
      return format(tradeDate, 'yyyy-MM-dd') === dayStr;
    });
    
    if (dayTrades.length === 0) return null;
    
    const totalPnL = dayTrades.reduce((sum, trade) => {
      return sum + (trade.profit_usd || 0);
    }, 0);
    
    // Calculate percentage based on capital at start of day
    const capitalAtStart = getCapitalAtStartOfDay(day);
    const pnlPercent = capitalAtStart && capitalAtStart > 0 
      ? (totalPnL / capitalAtStart) * 100 
      : null;
    
    return { usd: totalPnL, percent: pnlPercent };
  };

  // Get calendar weeks (array of week arrays)
  const getCalendarWeeks = () => {
    const days = getCalendarDays();
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  };

  // Calculate weekly PnL from a week's days (returns { usd, percent })
  const getWeeklyPnL = (weekDays) => {
    let hasAnyTrades = false;
    let totalPnL = 0;
    
    weekDays.forEach(day => {
      const dailyResult = getDailyPnL(day);
      if (dailyResult !== null) {
        hasAnyTrades = true;
        totalPnL += dailyResult.usd;
      }
    });
    
    if (!hasAnyTrades) return null;
    
    // Calculate percentage based on capital at start of week
    const weekStart = weekDays[0];
    const capitalAtStart = getCapitalAtStartOfWeek(weekStart);
    const pnlPercent = capitalAtStart && capitalAtStart > 0 
      ? (totalPnL / capitalAtStart) * 100 
      : null;
    
    return { usd: totalPnL, percent: pnlPercent };
  };

  // Notes drawer handlers (same pattern as OpenPositions)
  const openNotesDrawer = (trade, e) => {
    if (e) e.stopPropagation();
    setNotesDrawerTrade(trade);
    setDrawerNote(trade.notes || "");
  };

  const closeNotesDrawer = () => {
    setNotesDrawerTrade(null);
    setDrawerNote("");
  };

  // Close drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && notesDrawerTrade) {
        closeNotesDrawer();
      }
    };
    
    if (notesDrawerTrade) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [notesDrawerTrade]);

  const handleSaveNote = async () => {
    if (!notesDrawerTrade) return;
    
    // Block note saving in shared/public profile mode
    if (isSharedProfileView) {
      toast.error("Cannot edit notes in view-only mode");
      return;
    }
    
    setSavingNote(true);
    try {
      await api.put(`/trades/${notesDrawerTrade.id}`, {
        notes: drawerNote
      });
      
      // Update local trades state
      setTrades(trades.map(t => 
        t.id === notesDrawerTrade.id ? { ...t, notes: drawerNote } : t
      ));
      
      // Update selected trade if it's the same
      if (selectedTrade?.id === notesDrawerTrade.id) {
        setSelectedTrade({ ...selectedTrade, notes: drawerNote });
      }
      
      // Update drawer trade
      setNotesDrawerTrade({ ...notesDrawerTrade, notes: drawerNote });
      
      toast.success("Notes saved");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] flex items-center justify-center" data-testid="history-loading">
        <GeniLoader size="md" />
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="history-empty">
        <div className="w-full max-w-[1600px] mx-auto">
          <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-8" data-testid="history-title">
            {t('history.title')}
          </h2>
          <div className="flex flex-col items-center justify-center min-h-[400px] text-white/40">
            <ChartLine size={64} weight="duotone" className="mb-4" />
            <p className="text-center text-lg">{t('history.noTradesYet')}</p>
            <p className="text-center text-sm text-white/30 mt-2">{t('history.uploadFirst')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0A0A0A] p-4 md:p-8" data-testid="history-page">
      <div className="w-full max-w-[1600px] mx-auto">
        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2" data-testid="history-title">
            {t('history.title')}
          </h2>
          <p className="text-white/60 text-base font-sans">
            {viewMode === 'recent-rr' 
              ? `${filteredTrades.length} ${rrSubsetView === 'recent' ? (language === 'ptBR' ? 'trades recentes' : 'recent trades') : (language === 'ptBR' ? 'trades anteriores' : 'previous trades')} ${language === 'ptBR' ? 'para análise de RR' : 'for RR analysis'}` 
              : viewMode === 'winning-rr'
              ? `${filteredTrades.length} ${filteredTrades.length !== 1 ? (language === 'ptBR' ? 'trades vencedores' : 'winning trades') : (language === 'ptBR' ? 'trade vencedor' : 'winning trade')} ${language === 'ptBR' ? 'para análise de RR' : 'for RR analysis'}`
              : viewMode === 'losing-streak'
              ? `${filteredTrades.length} ${filteredTrades.length !== 1 ? 'trades' : 'trade'} ${language === 'ptBR' ? 'dos seus dias de sequência de perdas' : 'from your losing streak days'}`
              : `${filteredTrades.length} ${filteredTrades.length === 1 ? "trade" : "trades"} ${tradeTypeFilter ? (language === 'ptBR' ? 'filtrados' : 'filtered') : (language === 'ptBR' ? 'analisados' : 'analyzed')}`
            }
          </p>
        </div>

        {/* Trade Type Filter Banner */}
        {tradeTypeFilter && (
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center justify-between"
            data-testid="filter-banner"
          >
            <div className="flex items-center gap-3">
              <Funnel size={20} weight="fill" className="text-[#00B8D4]" />
              <span className="text-white/80">
                {language === 'ptBR' ? 'Filtrado por:' : 'Filtered by:'} <span className="font-bold text-white">{getFilterLabel()}</span>
              </span>
              <span className="text-white/40 text-sm">
                ({filteredTrades.length} {language === 'ptBR' ? 'de' : 'of'} {trades.length} trades)
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearTradeTypeFilter}
              className="bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
              data-testid="clear-filter-btn"
            >
              <X size={14} className="mr-1" />
              {t('history.clearFilter')}
            </Button>
          </div>
        )}

        {/* Setup Group Filter Banner (from Analytics drilldown) */}
        {setupGroupId && (
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center justify-between"
            data-testid="setup-group-filter-banner"
          >
            <div className="flex items-center gap-3">
              <Funnel size={20} weight="fill" className="text-[#00B8D4]" />
              <span className="text-white/80">
                {language === 'ptBR' ? 'Mostrando trades de:' : 'Showing trades from:'}{' '}
                <span className="font-bold text-white">{setupGroupName || 'Setup Group'}</span>
              </span>
              <span className="text-white/40 text-sm">
                ({filteredTrades.length} {filteredTrades.length === 1 ? 'trade' : 'trades'})
              </span>
              {periodFromUrl && (
                <span className="text-white/30 text-xs font-mono ml-2">
                  • {periodFromUrl === '7' || periodFromUrl === '7d' ? 'Last 7 days' : periodFromUrl === '30' || periodFromUrl === '30d' ? 'Last 30 days' : periodFromUrl === '90' || periodFromUrl === '90d' ? 'Last 90 days' : ''}
                </span>
              )}
              {startDateFromUrl && endDateFromUrl && (
                <span className="text-white/30 text-xs font-mono ml-2">
                  • Custom range
                </span>
              )}
              {directionFromUrl && directionFromUrl !== 'all' && (
                <span className="text-white/30 text-xs font-mono ml-2">
                  • {directionFromUrl.toUpperCase()} only
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearSetupGroupFilter}
              className="bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
              data-testid="clear-setup-group-filter-btn"
            >
              <X size={14} className="mr-1" />
              {language === 'ptBR' ? 'Ver todos os trades' : 'View all trades'}
            </Button>
          </div>
        )}

        {!setupGroupId && analyticsScope === 'eligible_closed' && startDateFromUrl && endDateFromUrl && (
          <div
            className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center justify-between"
            data-testid="monthly-performance-filter-banner"
          >
            <div className="flex items-center gap-3">
              <Funnel size={20} weight="fill" className="text-[#00B8D4]" />
              <span className="text-white/80">
                {language === 'ptBR' ? 'Mostrando mês de:' : 'Showing month:'}{' '}
                <span className="font-bold text-white">{monthlyPerformanceLabel || (language === 'ptBR' ? 'Período selecionado' : 'Selected range')}</span>
              </span>
              <span className="text-white/40 text-sm">
                ({filteredTrades.length} {filteredTrades.length === 1 ? 'trade' : 'trades'})
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAnalyticsDrilldownFilter}
              className="bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
              data-testid="clear-monthly-performance-filter-btn"
            >
              <X size={14} className="mr-1" />
              {language === 'ptBR' ? 'Ver todos os trades' : 'View all trades'}
            </Button>
          </div>
        )}

        {/* Asset Group Filter Banner (from Advanced Analytics drilldown) */}
        {assetGroupFilter && (
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm p-4 mb-6 flex items-center justify-between"
            data-testid="asset-group-filter-banner"
          >
            <div className="flex items-center gap-3">
              <Funnel size={20} weight="fill" className="text-[#00B8D4]" />
              <span className="text-white/80">
                {language === 'ptBR' ? 'Asset Group:' : 'Asset Group:'}{' '}
                <span className="font-bold text-white">{assetGroupName || 'Asset Group'}</span>
              </span>
              <span className="text-white/40 text-sm">
                ({filteredTrades.length} {filteredTrades.length === 1 ? 'trade' : 'trades'})
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAssetGroupFilter}
              className="bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
              data-testid="clear-asset-group-filter-btn"
            >
              <X size={14} className="mr-1" />
              {language === 'ptBR' ? 'Ver todos os trades' : 'View all trades'}
            </Button>
          </div>
        )}

        {/* Insight View Mode Banner */}
        {viewMode && getViewModeDescription() && (
          <div 
            className="bg-[#FFC107]/5 border border-[#FFC107]/20 rounded-sm p-4 mb-6 flex items-center justify-between"
            data-testid="view-mode-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded flex items-center justify-center bg-[#FFC107]/15">
                <Warning size={18} weight="bold" className="text-[#FFC107]" />
              </div>
              <div>
                <span className="text-white/90 font-semibold block">
                  {getViewModeDescription().title}
                </span>
                <span className="text-white/50 text-sm">
                  {getViewModeDescription().description}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearViewMode}
              className="bg-transparent border-[#FFC107]/30 text-[#FFC107]/80 hover:bg-[#FFC107]/10 hover:text-[#FFC107]"
              data-testid="clear-view-mode-btn"
            >
              <X size={14} className="mr-1" />
              Show All
            </Button>
          </div>
        )}

        {/* RR Comparison Summary (for recent-rr view mode) */}
        {viewMode === 'recent-rr' && rrComparisonData && (
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-6"
            data-testid="rr-comparison-summary"
          >
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-4">
              RR Comparison · Why This Warning Appeared
            </div>
            
            {rrComparisonData.hasEnoughData ? (
              <div className="grid grid-cols-3 gap-4">
                {/* Recent 10 Trades - Clickable */}
                <div 
                  onClick={() => setRrSubsetView('recent')}
                  className={`rounded-sm p-4 cursor-pointer transition-all duration-200 ${
                    rrSubsetView === 'recent'
                      ? 'bg-[#00B8D4]/10 border-2 border-[#00B8D4]/50 ring-1 ring-[#00B8D4]/20'
                      : 'bg-[#0A0A0A] border border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
                  }`}
                  data-testid="rr-recent-card"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-mono ${rrSubsetView === 'recent' ? 'text-[#00B8D4]' : 'text-white/40'}`}>
                      RECENT 10 TRADES
                    </span>
                    {rrSubsetView === 'recent' && (
                      <span className="text-[10px] font-mono text-[#00B8D4]/70 bg-[#00B8D4]/10 px-1.5 py-0.5 rounded">
                        SHOWING
                      </span>
                    )}
                  </div>
                  <div className={`text-2xl font-bold font-mono ${
                    rrComparisonData.recentAvgRR >= 0 ? 'text-white' : 'text-[#FF3B30]'
                  }`}>
                    {rrComparisonData.recentAvgRR >= 0 ? '+' : ''}{rrComparisonData.recentAvgRR.toFixed(2)}R
                  </div>
                  <div className="text-white/30 text-xs mt-1">
                    avg RR ({rrComparisonData.recentCount} with RR data)
                  </div>
                </div>
                
                {/* Previous 10 Trades - Clickable */}
                <div 
                  onClick={() => setRrSubsetView('previous')}
                  className={`rounded-sm p-4 cursor-pointer transition-all duration-200 ${
                    rrSubsetView === 'previous'
                      ? 'bg-[#00B8D4]/10 border-2 border-[#00B8D4]/50 ring-1 ring-[#00B8D4]/20'
                      : 'bg-[#0A0A0A] border border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
                  }`}
                  data-testid="rr-previous-card"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-mono ${rrSubsetView === 'previous' ? 'text-[#00B8D4]' : 'text-white/40'}`}>
                      PREVIOUS 10 TRADES
                    </span>
                    {rrSubsetView === 'previous' && (
                      <span className="text-[10px] font-mono text-[#00B8D4]/70 bg-[#00B8D4]/10 px-1.5 py-0.5 rounded">
                        SHOWING
                      </span>
                    )}
                  </div>
                  <div className={`text-2xl font-bold font-mono ${
                    rrComparisonData.previousAvgRR >= 0 ? 'text-white' : 'text-[#FF3B30]'
                  }`}>
                    {rrComparisonData.previousAvgRR >= 0 ? '+' : ''}{rrComparisonData.previousAvgRR.toFixed(2)}R
                  </div>
                  <div className="text-white/30 text-xs mt-1">
                    avg RR ({rrComparisonData.previousCount} with RR data)
                  </div>
                </div>
                
                {/* Difference - Not clickable */}
                <div className={`rounded-sm p-4 ${
                  rrComparisonData.isImproving 
                    ? 'bg-[#00E676]/10 border border-[#00E676]/20' 
                    : 'bg-[#FF3B30]/10 border border-[#FF3B30]/20'
                }`}>
                  <div className="text-white/40 text-xs font-mono mb-1">DIFFERENCE</div>
                  <div className={`text-2xl font-bold font-mono flex items-center gap-2 ${
                    rrComparisonData.isImproving ? 'text-[#00E676]' : 'text-[#FF3B30]'
                  }`}>
                    {rrComparisonData.isImproving ? (
                      <TrendUp size={20} weight="bold" />
                    ) : (
                      <TrendDown size={20} weight="bold" />
                    )}
                    {rrComparisonData.difference >= 0 ? '+' : ''}{rrComparisonData.difference.toFixed(2)}R
                  </div>
                  <div className={`text-xs mt-1 ${
                    rrComparisonData.isImproving ? 'text-[#00E676]/60' : 'text-[#FF3B30]/60'
                  }`}>
                    {Math.abs(rrComparisonData.deltaPercent).toFixed(0)}% {rrComparisonData.isImproving ? 'improvement' : 'decline'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-white/50 text-sm text-center py-4">
                Not enough trade data for comparison (need at least 20 closed trades)
              </div>
            )}
          </div>
        )}

        {/* Winning RR Comparison Summary (for winning-rr view mode) */}
        {viewMode === 'winning-rr' && winningRrComparisonData && (
          <div 
            className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-6"
            data-testid="winning-rr-comparison-summary"
          >
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-4">
              Winning Trade RR · Why This Warning Appeared
            </div>
            
            {winningRrComparisonData.hasEnoughData ? (
              <>
                {/* Trend Comparison (if trend data available) */}
                {conditionType === 'trend' && winningRrComparisonData.hasTrendData ? (
                  <div className="grid grid-cols-3 gap-4">
                    {/* Recent 5 Wins - Clickable */}
                    <div 
                      onClick={() => setWinningRrSubsetView('recent')}
                      className={`rounded-sm p-4 cursor-pointer transition-all duration-200 ${
                        winningRrSubsetView === 'recent'
                          ? 'bg-[#00B8D4]/10 border-2 border-[#00B8D4]/50 ring-1 ring-[#00B8D4]/20'
                          : 'bg-[#0A0A0A] border border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
                      }`}
                      data-testid="winning-rr-recent-card"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-mono ${winningRrSubsetView === 'recent' ? 'text-[#00B8D4]' : 'text-white/40'}`}>
                          RECENT 5 WINS
                        </span>
                        {winningRrSubsetView === 'recent' && (
                          <span className="text-[10px] font-mono text-[#00B8D4]/70 bg-[#00B8D4]/10 px-1.5 py-0.5 rounded">
                            SHOWING
                          </span>
                        )}
                      </div>
                      <div className={`text-2xl font-bold font-mono ${
                        winningRrComparisonData.recentWinRR >= 1.0 ? 'text-white' : 'text-[#FF3B30]'
                      }`}>
                        {winningRrComparisonData.recentWinRR >= 0 ? '+' : ''}{winningRrComparisonData.recentWinRR.toFixed(2)}R
                      </div>
                      <div className="text-white/30 text-xs mt-1">
                        avg win RR
                      </div>
                    </div>
                    
                    {/* Previous 5 Wins - Clickable */}
                    <div 
                      onClick={() => setWinningRrSubsetView('previous')}
                      className={`rounded-sm p-4 cursor-pointer transition-all duration-200 ${
                        winningRrSubsetView === 'previous'
                          ? 'bg-[#00B8D4]/10 border-2 border-[#00B8D4]/50 ring-1 ring-[#00B8D4]/20'
                          : 'bg-[#0A0A0A] border border-white/10 hover:border-white/20 hover:bg-white/[0.02]'
                      }`}
                      data-testid="winning-rr-previous-card"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-mono ${winningRrSubsetView === 'previous' ? 'text-[#00B8D4]' : 'text-white/40'}`}>
                          PREVIOUS 5 WINS
                        </span>
                        {winningRrSubsetView === 'previous' && (
                          <span className="text-[10px] font-mono text-[#00B8D4]/70 bg-[#00B8D4]/10 px-1.5 py-0.5 rounded">
                            SHOWING
                          </span>
                        )}
                      </div>
                      <div className={`text-2xl font-bold font-mono ${
                        winningRrComparisonData.previousWinRR >= 1.0 ? 'text-white' : 'text-[#FF3B30]'
                      }`}>
                        {winningRrComparisonData.previousWinRR >= 0 ? '+' : ''}{winningRrComparisonData.previousWinRR.toFixed(2)}R
                      </div>
                      <div className="text-white/30 text-xs mt-1">
                        avg win RR
                      </div>
                    </div>
                    
                    {/* Difference - Not clickable */}
                    <div className={`rounded-sm p-4 ${
                      winningRrComparisonData.difference >= 0
                        ? 'bg-[#00E676]/10 border border-[#00E676]/20' 
                        : 'bg-[#FF3B30]/10 border border-[#FF3B30]/20'
                    }`}>
                      <div className="text-white/40 text-xs font-mono mb-1">DIFFERENCE</div>
                      <div className={`text-2xl font-bold font-mono flex items-center gap-2 ${
                        winningRrComparisonData.difference >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                      }`}>
                        {winningRrComparisonData.difference >= 0 ? (
                          <TrendUp size={20} weight="bold" />
                        ) : (
                          <TrendDown size={20} weight="bold" />
                        )}
                        {winningRrComparisonData.difference >= 0 ? '+' : ''}{winningRrComparisonData.difference.toFixed(2)}R
                      </div>
                      <div className={`text-xs mt-1 ${
                        winningRrComparisonData.difference >= 0 ? 'text-[#00E676]/60' : 'text-[#FF3B30]/60'
                      }`}>
                        {winningRrComparisonData.difference >= 0 ? 'improvement' : 'decline'}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Static Warning Summary */
                  <div className="grid grid-cols-2 gap-4">
                    {/* Overall Avg Win RR */}
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-1">YOUR AVG WIN RR</div>
                      <div className={`text-2xl font-bold font-mono ${
                        winningRrComparisonData.avgWinRR >= 1.2 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                      }`}>
                        {winningRrComparisonData.avgWinRR >= 0 ? '+' : ''}{winningRrComparisonData.avgWinRR.toFixed(2)}R
                      </div>
                      <div className="text-white/30 text-xs mt-1">
                        across {winningRrComparisonData.totalWinningCount} winning trades
                      </div>
                    </div>
                    
                    {/* Target Threshold */}
                    <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-1">OPTIMAL THRESHOLD</div>
                      <div className="text-2xl font-bold font-mono text-white/60">
                        +1.20R
                      </div>
                      <div className="text-white/30 text-xs mt-1">
                        minimum recommended avg win RR
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Additional context for static warning */}
                {conditionType === 'static' && (
                  <div className="mt-4 p-3 bg-[#FFC107]/5 border border-[#FFC107]/10 rounded-sm">
                    <p className="text-white/60 text-xs">
                      Your winning trades are averaging {winningRrComparisonData.avgWinRR.toFixed(2)}R, which is below the 1.2R threshold. Consider reviewing your exit strategy or letting winners run longer.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-white/50 text-sm text-center py-4">
                Not enough winning trade data for analysis (need at least 5 winning trades)
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px md:gap-6">
          {/* Trade List */}
          <div className="bg-[#121212] border border-white/10 rounded-sm" data-testid="trades-list">
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between animate-fade-in">
                <h3 className="text-lg font-bold font-heading">All Trades</h3>
                <div className="flex items-center gap-3">
                  {/* Discrete Todos | 7D | 30D Filter UI */}
                  <div className="flex items-center bg-[#0A0A0A] border border-white/10 rounded-sm p-0.5" data-testid="time-filter-container">
                    {['Todos', '7D', '30D'].map((period) => (
                      <button
                        key={period}
                        onClick={() => setTimeFilter(period)}
                        data-testid={`time-filter-${period.toLowerCase()}`}
                        className={`px-3 py-1 text-xs rounded-sm transition-all duration-200 font-mono font-bold ${
                          timeFilter === period
                            ? 'bg-white text-black'
                            : 'text-white/40 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                  {calendarFilter && (
                    <span className="text-xs text-white/50" data-testid="calendar-filter-count">
                      {filteredTrades.length} trade{filteredTrades.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Calendar Filter Indicator */}
            {calendarFilter && (
              <div className="px-4 py-3 bg-[#00B8D4]/5 border-b border-[#00B8D4]/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarBlank size={16} className="text-[#00B8D4]" />
                  <span className="text-sm text-white/80">{getCalendarFilterLabel()}</span>
                </div>
                <button
                  onClick={clearCalendarFilter}
                  className="text-xs text-[#00B8D4] hover:text-[#00B8D4]/80 flex items-center gap-1"
                >
                  <X size={12} />
                  Clear filter
                </button>
              </div>
            )}
            
            <SyncedScrollTable maxHeight="calc(100vh - 280px)">
              <table className="w-full">
                <thead className="bg-[#0A0A0A] sticky top-0">
                  <tr className="text-left text-xs text-white/40 font-mono">
                    <th className="p-3">ASSET</th>
                    <th className="p-3">DIRECTION</th>
                    <th className="p-3">STATUS</th>
                    <th className="p-3">{isSharedProfileView ? 'PNL (R)' : 'RR'}</th>
                    {!isSharedProfileView && <th className="p-3">PROFIT</th>}
                    <th className="p-3">DATE</th>
                    {!isSharedProfileView && <th className="p-3">NOTES</th>}
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Empty state when Setup Group filter has no matching trades OR when setup_ids are missing */}
                  {(filteredTrades.length === 0 && setupGroupId) || (setupGroupId && setupGroupSetupIds.length === 0) ? (
                    <tr>
                      <td colSpan={isSharedProfileView ? 6 : 8} className="p-12 text-center">
                        <div className="text-white/40 text-sm">
                          {language === 'ptBR' 
                            ? 'Nenhum trade encontrado para este grupo de estratégias.' 
                            : 'No trades found for this strategy group.'}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSetupGroupFilter}
                          className="mt-4 bg-transparent border-white/10 text-white/70 hover:bg-white/5 hover:text-white"
                          data-testid="empty-state-view-all-btn"
                        >
                          {language === 'ptBR' ? 'Ver todos os trades' : 'View all trades'}
                        </Button>
                      </td>
                    </tr>
                  ) : (
                  filteredTrades.map((trade) => (
                    <tr
                      key={trade.id}
                      ref={highlightedTradeId === trade.id ? highlightedRef : null}
                      data-testid={`trade-row-${trade.id}`}
                      onClick={() => setSelectedTrade(trade)}
                      className={`trade-row border-t border-white/10 cursor-pointer transition-all duration-300 ${
                        selectedTrade?.id === trade.id ? "bg-white/5" : ""
                      } ${
                        highlightedTradeId === trade.id 
                          ? "bg-[#FFC107]/10 ring-1 ring-[#FFC107]/30 animate-pulse" 
                          : ""
                      }`}
                    >
                      <td className="p-3">
                        <div className="font-mono text-white font-bold">{trade.asset}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-white/40 uppercase">{trade.trade_status}</span>
                          {/* Trade Type Badge - Editable for closed trades if user has permission */}
                          {trade.trade_type && (
                            canEditClosedTradeType && trade.status === 'closed' && !isSharedProfileView ? (
                              /* Editable: Click cycles through Scalp → Day → Swing */
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const currentType = trade.trade_type || 'day_trade';
                                  let newType;
                                  if (currentType === 'scalp') {
                                    newType = 'day_trade';
                                  } else if (currentType === 'day_trade') {
                                    newType = 'swing_trade';
                                  } else {
                                    newType = 'scalp';
                                  }
                                  handleUpdateTradeType(trade.id, newType);
                                }}
                                disabled={updatingTradeTypeId === trade.id}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all cursor-pointer hover:opacity-80 ${
                                  trade.trade_type === 'swing_trade'
                                    ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                                    : trade.trade_type === 'scalp'
                                    ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                                    : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                }`}
                                data-testid={`trade-type-edit-${trade.id}`}
                                title="Click to cycle trade type: Scalp → Day → Swing"
                              >
                                {updatingTradeTypeId === trade.id ? (
                                  <ArrowsClockwise size={10} className="animate-spin" />
                                ) : trade.trade_type === 'swing_trade' ? (
                                  <Moon size={10} weight="fill" />
                                ) : trade.trade_type === 'scalp' ? (
                                  <Lightning size={10} weight="fill" />
                                ) : (
                                  <Sun size={10} weight="fill" />
                                )}
                                {trade.trade_type === 'swing_trade' ? 'Swing' : trade.trade_type === 'scalp' ? 'Scalp' : 'Day'}
                              </button>
                            ) : (
                              /* Read-only: Standard TradeTypeBadge */
                              <TradeTypeBadge tradeType={trade.trade_type} size="xs" />
                            )
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {trade.direction === "long" ? (
                            <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                          ) : (
                            <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                          )}
                          <span
                            className={`font-mono text-sm uppercase ${
                              trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                            }`}
                            data-testid={`trade-direction-${trade.id}`}
                          >
                            {trade.direction}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                          trade.status === "open" 
                            ? "bg-[#FFC107]/10 text-[#FFC107]" 
                            : trade.status === "canceled"
                            ? "bg-[#FFA726]/10 text-[#FFA726]"
                            : trade.outcome === "win"
                            ? "bg-[#00E676]/10 text-[#00E676]"
                            : trade.outcome === "loss"
                            ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                            : "bg-white/10 text-white/60"
                        }`}>
                          {trade.status === "open" ? "OPEN" : 
                           trade.status === "canceled" ? "NOT TRIGGERED" :
                           trade.outcome?.toUpperCase() || "CLOSED"}
                        </span>
                      </td>
                      <td className="p-3">
                        {isSharedProfileView ? (
                          /* Public Mode: Show realized RR (PNL in R) for closed trades, "—" for open */
                          trade.status === 'closed' && trade.profit_usd !== null && trade.profit_usd !== undefined ? (
                            <span 
                              className={`font-mono font-bold ${
                                trade.rr > 0 ? 'text-[#00E676]' : 
                                trade.rr < 0 ? 'text-[#FF3B30]' : 
                                'text-white/60'
                              }`}
                              data-testid={`trade-pnl-r-${trade.id}`}
                            >
                              {trade.rr > 0 ? '+' : ''}{(trade.rr || 0).toFixed(2)}R
                            </span>
                          ) : (
                            /* OPEN trades or trades without realized result show "—" */
                            <span className="font-mono text-white/40" data-testid={`trade-pnl-r-${trade.id}`}>
                              —
                            </span>
                          )
                        ) : (
                          /* Private Mode: Show planned RR */
                          <span className="font-mono text-white" data-testid={`trade-rr-${trade.id}`}>
                            {trade.rr.toFixed(2)}
                          </span>
                        )}
                      </td>
                      {!isSharedProfileView && (
                        <td className="p-3" style={{ minWidth: '140px' }}>
                          {/* Profit Display - Only shown in private mode */}
                          {trade.profit_usd !== null && trade.profit_usd !== undefined ? (
                            <div className="flex items-center gap-1.5">
                              <span 
                                className={`font-mono font-bold ${
                                  trade.profit_usd > 0 ? 'text-[#00E676]' : 
                                  trade.profit_usd < 0 ? 'text-[#FF3B30]' : 
                                  'text-white/60'
                                }`}
                                data-testid={`trade-profit-${trade.id}`}
                              >
                                {trade.profit_usd > 0 ? '+' : ''}${trade.profit_usd.toFixed(2)}
                              </span>
                              {trade.profit_adjusted_manually && (
                                <span 
                                  className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                    trade.profit_adjustment_reason === 'partial_profit' 
                                      ? 'bg-[#00E676]/15 text-[#00E676]/80' 
                                      : 'bg-[#FFC107]/15 text-[#FFC107]/80'
                                  }`}
                                  data-testid={`trade-adjusted-label-${trade.id}`}
                                >
                                  {trade.profit_adjustment_reason === 'partial_profit' ? 'Partial' : 'Reduced'}
                                </span>
                              )}
                              {trade.has_hedge && (
                                <span 
                                  className={`inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded font-medium ${
                                    trade.hedge_classification === 'beneficial' 
                                      ? 'bg-cyan-500/15 text-cyan-400' 
                                      : trade.hedge_classification === 'harmful'
                                      ? 'bg-cyan-500/10 text-cyan-400/70'
                                      : 'bg-cyan-500/10 text-cyan-400/60'
                                  }`}
                                  data-testid={`trade-hedge-badge-${trade.id}`}
                                  title={`Hedge: ${trade.hedge_impact >= 0 ? '+' : ''}$${(trade.hedge_impact || 0).toFixed(2)}`}
                                >
                                  <Shield size={10} weight="fill" />
                                  {trade.hedge_classification === 'beneficial' ? '+' : trade.hedge_classification === 'harmful' ? '-' : '='}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-white/40 text-xs">-</span>
                          )}
                        </td>
                      )}
                      <td className="p-3">
                        <span className="text-xs text-white/60">
                          {format(new Date(trade.created_at), "MMM d, yyyy")}
                        </span>
                      </td>
                      {!isSharedProfileView && (
                        <td className="p-3">
                          {/* Notes Badge - Hidden in public mode, same style as OpenPositions */}
                          <button
                            onClick={(e) => openNotesDrawer(trade, e)}
                            data-testid={`notes-btn-${trade.id}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors hover:bg-white/10 ${
                              trade.notes 
                                ? "bg-white/10 text-white/70 border border-white/20"
                                : "bg-white/5 text-white/40 border border-white/10"
                            }`}
                          >
                            <Note size={12} weight={trade.notes ? "fill" : "regular"} />
                            {trade.notes ? "1 note" : "Notes"}
                          </button>
                        </td>
                      )}
                      <td className="p-3 w-20">
                        {/* Cancel button - only show for OPEN trades in PRIVATE mode */}
                        {trade.status === 'open' && !isSharedProfileView && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCancelId(trade.id);
                            }}
                            data-testid={`cancel-trade-${trade.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-white/40 hover:text-[#FFC107] hover:bg-[#FFC107]/10 text-xs px-2"
                          >
                            <XCircle size={14} weight="duotone" className="mr-1" />
                            Cancel
                          </Button>
                        )}
                        {trade.status === 'canceled' && (
                          <span className="text-xs text-[#FFA726]/60 italic">Not triggered</span>
                        )}
                        {/* Reopen Trade button - only for CLOSED trades within 12h of closing, PRIVATE mode */}
                        {isReopenEligible(trade) && !isSharedProfileView && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReopenId(trade.id);
                            }}
                            data-testid={`reopen-trade-${trade.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-white/40 hover:text-[#00E676] hover:bg-[#00E676]/10 text-xs px-2"
                          >
                            <ArrowsClockwise size={14} weight="bold" className="mr-1" />
                            Reopen
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </SyncedScrollTable>
          </div>

          {/* Trade Details */}
          <div className="bg-[#121212] border border-white/10 rounded-sm" data-testid="trade-details">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-bold font-heading">Trade Details</h3>
            </div>
            
            {!selectedTrade ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-white/40" data-testid="no-trade-selected">
                <MagnifyingGlass size={48} weight="duotone" className="mb-4" />
                <p className="text-center">Select a trade to view details</p>
              </div>
            ) : (
              <div className="p-6 space-y-4 overflow-auto max-h-[calc(100vh-240px)]" data-testid="trade-detail-view">
                {selectedTrade.image_data && (
                  <div className="border border-white/10 rounded-sm overflow-hidden" data-testid="trade-chart-image">
                    <img
                      src={`data:image/png;base64,${selectedTrade.image_data}`}
                      alt="Trading chart"
                      className="w-full h-auto"
                    />
                  </div>
                )}

                <div className="bg-[#000000] border border-white/10 rounded-sm p-4 font-mono text-sm">
                  <div className={`grid ${isSharedProfileView ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    {/* TRADE STATUS - Hidden in public mode */}
                    {!isSharedProfileView && (
                      <div>
                        <div className="text-white/40 text-xs mb-1">TRADE STATUS</div>
                        <div className="text-white uppercase" data-testid="detail-trade-status">{selectedTrade.trade_status}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-white/40 text-xs mb-1">DIRECTION</div>
                      <div
                        className={`font-bold uppercase ${
                          selectedTrade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                        }`}
                        data-testid="detail-direction"
                      >
                        {selectedTrade.direction}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#000000] border border-white/10 rounded-sm p-4 font-mono text-sm">
                  <div className="text-white/40 text-xs mb-2">ASSET</div>
                  <div className="flex items-center gap-3">
                    <span className="text-white text-lg font-bold" data-testid="detail-asset">{selectedTrade.asset}</span>
                    {/* Trade Type Badge in Details - Editable for closed trades if user has permission */}
                    {selectedTrade.trade_type && (
                      canEditClosedTradeType && selectedTrade.status === 'closed' && !isSharedProfileView ? (
                        /* Editable: Click cycles through Scalp → Day → Swing */
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentType = selectedTrade.trade_type || 'day_trade';
                            let newType;
                            if (currentType === 'scalp') {
                              newType = 'day_trade';
                            } else if (currentType === 'day_trade') {
                              newType = 'swing_trade';
                            } else {
                              newType = 'scalp';
                            }
                            handleUpdateTradeType(selectedTrade.id, newType);
                          }}
                          disabled={updatingTradeTypeId === selectedTrade.id}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer hover:opacity-80 ${
                            selectedTrade.trade_type === 'swing_trade'
                              ? "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                              : selectedTrade.trade_type === 'scalp'
                              ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                              : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                          }`}
                          data-testid={`detail-trade-type-edit`}
                          title="Click to cycle trade type: Scalp → Day → Swing"
                        >
                          {updatingTradeTypeId === selectedTrade.id ? (
                            <ArrowsClockwise size={12} className="animate-spin" />
                          ) : selectedTrade.trade_type === 'swing_trade' ? (
                            <Moon size={12} weight="fill" />
                          ) : selectedTrade.trade_type === 'scalp' ? (
                            <Lightning size={12} weight="fill" />
                          ) : (
                            <Sun size={12} weight="fill" />
                          )}
                          {selectedTrade.trade_type === 'swing_trade' ? 'Swing' : selectedTrade.trade_type === 'scalp' ? 'Scalp' : 'Day'}
                        </button>
                      ) : (
                        /* Read-only: Standard TradeTypeBadge */
                        <TradeTypeBadge tradeType={selectedTrade.trade_type} size="sm" />
                      )
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono">ENTRY</div>
                    <div className="text-white font-mono text-lg" data-testid="detail-entry">{selectedTrade.entry}</div>
                  </div>
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono flex items-center gap-1">
                      STOP LOSS
                      {selectedTrade.stop_adjusted && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-[#FFC107]/10 text-[#FFC107] border border-[#FFC107]/20">
                          Adjusted
                        </span>
                      )}
                      {/* Legacy Stop Correction - only for authorized accounts on closed trades with adjusted stop */}
                      {canEditClosedTradeType && selectedTrade.status === 'closed' && selectedTrade.stop_adjusted && !isSharedProfileView && editingLegacyStop !== selectedTrade.id && (
                        <button
                          onClick={() => {
                            setEditingLegacyStop(selectedTrade.id);
                            setLegacyStopValue(selectedTrade.stop_loss?.toString() || "");
                          }}
                          className="ml-auto text-white/40 hover:text-white transition-colors"
                          title="Correct adjusted stop (legacy repair)"
                          data-testid="legacy-stop-edit-btn"
                        >
                          <PencilSimple size={12} />
                        </button>
                      )}
                    </div>
                    
                    {/* Legacy Stop Edit Mode */}
                    {editingLegacyStop === selectedTrade.id ? (
                      <div className="space-y-2">
                        <input
                          type="number"
                          step="any"
                          value={legacyStopValue}
                          onChange={(e) => setLegacyStopValue(e.target.value)}
                          className="w-full bg-[#1a1a1a] border border-white/20 rounded px-2 py-1 text-white font-mono text-lg focus:border-[#00E676] focus:outline-none"
                          placeholder="Enter corrected stop"
                          autoFocus
                          data-testid="legacy-stop-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingLegacyStop(null);
                              setLegacyStopValue("");
                            }}
                            disabled={savingLegacyStop}
                            className="text-white/60 hover:text-white text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLegacyStopCorrection(selectedTrade.id)}
                            disabled={savingLegacyStop || !legacyStopValue}
                            className="bg-[#00E676] hover:bg-[#00E676]/80 text-black text-xs"
                            data-testid="legacy-stop-save-btn"
                          >
                            {savingLegacyStop ? (
                              <ArrowsClockwise size={12} className="animate-spin mr-1" />
                            ) : null}
                            Save & Recalculate
                          </Button>
                        </div>
                        <div className="text-[10px] text-white/30 font-mono">
                          This will recalculate RR using the corrected stop
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-white font-mono text-lg" data-testid="detail-stop-loss">{selectedTrade.stop_loss}</div>
                        {selectedTrade.stop_adjusted && selectedTrade.initial_stop_loss && selectedTrade.initial_stop_loss !== selectedTrade.stop_loss && (
                          <div className="text-white/30 text-xs font-mono mt-1">
                            Initial: ${selectedTrade.initial_stop_loss?.toLocaleString()}
                          </div>
                        )}
                        {selectedTrade.legacy_stop_corrected && (
                          <div className="text-[9px] text-[#00E676]/60 font-mono mt-1">
                            ✓ Corrected
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono">TAKE PROFIT</div>
                    <div className="text-white font-mono text-lg" data-testid="detail-take-profit">{selectedTrade.take_profit}</div>
                  </div>
                </div>

                <div className={`grid ${isSharedProfileView ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono">{isSharedProfileView ? 'PNL (R)' : 'RR RATIO'}</div>
                    <div className="text-white font-mono text-2xl font-bold" data-testid="detail-rr">
                      {isSharedProfileView && selectedTrade.status === 'closed' && selectedTrade.profit_usd !== null ? (
                        <span className={
                          selectedTrade.rr > 0 ? 'text-[#00E676]' : 
                          selectedTrade.rr < 0 ? 'text-[#FF3B30]' : 
                          'text-white'
                        }>
                          {selectedTrade.rr > 0 ? '+' : ''}{(selectedTrade.rr || 0).toFixed(2)}R
                        </span>
                      ) : isSharedProfileView && selectedTrade.status !== 'closed' ? (
                        <span className="text-white/40">—</span>
                      ) : (
                        selectedTrade.rr.toFixed(2)
                      )}
                    </div>
                  </div>
                  {/* CONFIDENCE - Hidden in public mode */}
                  {!isSharedProfileView && (
                    <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                      <div className="text-white/40 text-xs mb-1 font-mono">CONFIDENCE</div>
                      <div className="flex items-center" data-testid="detail-confidence">
                        {selectedTrade.confidence ? (
                          <>
                            <span className={`confidence-dot confidence-${selectedTrade.confidence}`}></span>
                            <span className={`font-mono uppercase ${
                              selectedTrade.confidence === 'high' ? 'text-[#00E676]' :
                              selectedTrade.confidence === 'medium' ? 'text-[#FFA726]' :
                              selectedTrade.confidence === 'low' ? 'text-[#FF3B30]' :
                              'text-white'
                            }`}>{selectedTrade.confidence}</span>
                          </>
                        ) : (
                          <span className="text-white/40 font-mono">Not tracked</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Profit Section - Read-Only Display for closed trades */}
                {selectedTrade.status === 'closed' && selectedTrade.profit_usd !== null && (
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    {isSharedProfileView ? (
                      /* Shared/Public Profile Mode: Show RR, hide USD */
                      <>
                        <div className="text-white/40 text-xs font-mono mb-2">REALIZED RR</div>
                        <div className={`font-mono text-2xl font-bold ${
                          selectedTrade.rr > 0 ? 'text-[#00E676]' : 
                          selectedTrade.rr < 0 ? 'text-[#FF3B30]' : 'text-white'
                        }`} data-testid="detail-profit-rr">
                          {selectedTrade.rr > 0 ? '+' : ''}{(selectedTrade.rr || 0).toFixed(2)}R
                        </div>
                      </>
                    ) : (
                      /* Normal Mode: Show USD */
                      <>
                        <div className="text-white/40 text-xs font-mono mb-2">PROFIT (USD)</div>
                        <div className={`font-mono text-2xl font-bold ${
                          selectedTrade.profit_usd > 0 ? 'text-[#00E676]' : 
                          selectedTrade.profit_usd < 0 ? 'text-[#FF3B30]' : 'text-white'
                        }`} data-testid="detail-profit">
                          {selectedTrade.profit_usd > 0 ? '+' : ''}${selectedTrade.profit_usd.toFixed(2)}
                        </div>
                      </>
                    )}
                    {selectedTrade.profit_adjusted_manually && (
                      <div className="mt-2 text-xs text-white/40 flex items-center gap-1">
                        <span className="bg-white/10 px-2 py-0.5 rounded-sm">
                          {selectedTrade.profit_adjustment_reason === 'partial_profit' ? 'Partial Profit' : 'Reduced Loss'}
                        </span>
                        <span>· Adjusted via partial execution</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Stop Management Section - Only for trades with adjusted stops */}
                {selectedTrade.stop_adjusted && (
                  <div className="bg-[#000000] border border-[#FFC107]/20 rounded-sm p-4" data-testid="stop-management-section">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[#FFC107]/80 text-xs font-mono uppercase">Stop Management</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FFC107]/10 text-[#FFC107] border border-[#FFC107]/20">
                        Adjusted
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-white/40 text-xs mb-1">Initial Stop</div>
                        <div className="font-mono text-lg text-white/60">
                          ${selectedTrade.initial_stop_loss?.toLocaleString()}
                        </div>
                        {!isSharedProfileView && (
                          <div className="text-white/30 text-xs">
                            Risk: ${selectedTrade.initial_risk_amount?.toFixed(2) || selectedTrade.hand_size?.toFixed(2)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-white/40 text-xs mb-1">Final Stop</div>
                        <div className="font-mono text-lg text-[#FF3B30]">
                          ${selectedTrade.stop_loss?.toLocaleString()}
                        </div>
                        {!isSharedProfileView && selectedTrade.current_risk_amount && (
                          <div className="text-white/30 text-xs">
                            Risk: ${selectedTrade.current_risk_amount.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {!isSharedProfileView && (
                      <div className="mt-3 pt-3 border-t border-white/10 text-white/40 text-xs">
                        Official RR calculated using initial risk (${selectedTrade.initial_risk_amount?.toFixed(2) || selectedTrade.hand_size?.toFixed(2)})
                      </div>
                    )}
                  </div>
                )}

                {/* Hedge Details Section - Only for hedged closed trades */}
                {selectedTrade.status === 'closed' && selectedTrade.has_hedge && (
                  <div className="bg-[#000000] border border-cyan-500/20 rounded-sm p-4" data-testid="hedge-details-section">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Shield size={16} weight="duotone" className="text-cyan-400" />
                        <span className="text-cyan-400/80 text-xs font-mono uppercase">Hedge Analysis</span>
                      </div>
                      {(selectedTrade.hedge_count || 1) > 1 && (
                        <span className="text-cyan-400/50 text-xs">{selectedTrade.hedge_count} hedges</span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Without Hedge */}
                      <div>
                        <div className="text-white/40 text-xs mb-1">Without Hedge</div>
                        <div className={`font-mono text-lg ${
                          (selectedTrade.pnl_without_hedge || 0) >= 0 ? 'text-white/60' : 'text-white/60'
                        }`}>
                          {(selectedTrade.pnl_without_hedge || 0) >= 0 ? '+' : ''}${(selectedTrade.pnl_without_hedge || 0).toFixed(2)}
                        </div>
                        <div className="text-white/30 text-xs">
                          {(selectedTrade.rr_without_hedge || 0).toFixed(2)}R
                        </div>
                      </div>
                      
                      {/* With Hedge (Final) */}
                      <div>
                        <div className="text-white/40 text-xs mb-1">With Hedge</div>
                        <div className={`font-mono text-lg font-bold ${
                          (selectedTrade.pnl_with_hedge || 0) > 0 ? 'text-[#00E676]' : 
                          (selectedTrade.pnl_with_hedge || 0) < 0 ? 'text-[#FF3B30]' : 'text-white'
                        }`}>
                          {(selectedTrade.pnl_with_hedge || 0) >= 0 ? '+' : ''}${(selectedTrade.pnl_with_hedge || 0).toFixed(2)}
                        </div>
                        <div className="text-white/50 text-xs">
                          {(selectedTrade.rr_with_hedge || 0).toFixed(2)}R
                        </div>
                      </div>
                    </div>
                    
                    {/* Hedge Impact */}
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">Hedge Impact:</span>
                        <span className={`font-mono text-sm font-bold ${
                          selectedTrade.hedge_classification === 'beneficial' ? 'text-[#00E676]' :
                          selectedTrade.hedge_classification === 'harmful' ? 'text-[#FF3B30]' :
                          'text-white/60'
                        }`}>
                          {(selectedTrade.hedge_impact || 0) >= 0 ? '+' : ''}${(selectedTrade.hedge_impact || 0).toFixed(2)}
                        </span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedTrade.hedge_classification === 'beneficial' 
                          ? 'bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20' :
                        selectedTrade.hedge_classification === 'harmful' 
                          ? 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20' :
                          'bg-white/10 text-white/60 border border-white/20'
                      }`}>
                        {selectedTrade.hedge_classification?.toUpperCase() || 'NEUTRAL'}
                      </span>
                    </div>
                    
                    {/* Hedge Entries List (if multiple) */}
                    {selectedTrade.hedge_entries && selectedTrade.hedge_entries.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="text-white/40 text-xs mb-2">Hedge Entries</div>
                        <div className="space-y-1">
                          {selectedTrade.hedge_entries.map((entry, idx) => (
                            <div key={entry.id || idx} className="flex items-center justify-between text-xs bg-white/5 rounded px-2 py-1.5">
                              <span className={`font-mono ${(entry.value || 0) >= 0 ? 'text-cyan-400' : 'text-cyan-400'}`}>
                                {(entry.value || 0) >= 0 ? '+' : ''}${(entry.value || 0).toFixed(2)}
                              </span>
                              {entry.note && <span className="text-white/40 truncate max-w-[150px]">{entry.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Hedge Note (legacy single note) */}
                    {selectedTrade.hedge_note && (!selectedTrade.hedge_entries || selectedTrade.hedge_entries.length === 0) && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <div className="text-white/40 text-xs mb-1">Note</div>
                        <div className="text-white/70 text-sm">{selectedTrade.hedge_note}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancel Reason - only for canceled trades */}
                {selectedTrade.status === 'canceled' && (
                  <div className="bg-[#000000] border border-[#FFA726]/20 rounded-sm p-4">
                    <div className="text-[#FFA726]/60 text-xs font-mono mb-2">CANCEL REASON</div>
                    <div className="text-[#FFA726] text-sm font-medium">
                      {getCancelReasonLabel(selectedTrade.cancel_reason)}
                    </div>
                  </div>
                )}

                {/* Notes Section - Hidden in public mode */}
                {!isSharedProfileView && selectedTrade.notes && selectedTrade.notes.trim() !== "" && (
                  <div 
                    className="bg-[#000000] border border-white/10 rounded-sm p-4 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={(e) => openNotesDrawer(selectedTrade, e)}
                    data-testid="detail-notes-section"
                  >
                    <div className="text-white/40 text-xs mb-2 font-mono flex items-center gap-2">
                      <Note size={12} />
                      YOUR NOTES
                    </div>
                    <div className="text-white/80 text-sm" data-testid="detail-notes">{selectedTrade.notes}</div>
                    <div className="text-white/30 text-xs mt-2">Click to edit</div>
                  </div>
                )}

                {/* Add/Edit Notes Button - Hidden in public mode, shown when no notes exist */}
                {!isSharedProfileView && (!selectedTrade.notes || selectedTrade.notes.trim() === "") && (
                  <button
                    onClick={(e) => openNotesDrawer(selectedTrade, e)}
                    className="w-full bg-[#000000] border border-white/10 border-dashed rounded-sm p-4 cursor-pointer hover:bg-white/5 hover:border-white/20 transition-colors text-left"
                    data-testid="add-notes-btn"
                  >
                    <div className="flex items-center gap-2 text-white/40">
                      <Note size={16} />
                      <span className="text-sm">Add notes to this trade...</span>
                    </div>
                  </button>
                )}

                <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                  <div className="text-white/40 text-xs mb-1 font-mono">CREATED</div>
                  <div className="text-white/80 text-sm">
                    {format(new Date(selectedTrade.created_at), "PPpp")}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Calendar Section - HIDDEN in public/shared profile mode (contains sensitive financial data) */}
        {!isSharedProfileView && (
        <div className="mt-6 bg-[#121212] border border-white/10 rounded-sm" data-testid="trade-calendar">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarBlank size={18} weight="duotone" className="text-white/60" />
              <h3 className="text-base font-bold font-heading">Calendar</h3>
            </div>
            <div className="flex items-center gap-1">
              <Button
                onClick={goToPrevMonth}
                variant="ghost"
                size="sm"
                className="text-white/60 hover:text-white hover:bg-white/5 p-1.5 h-7 w-7"
                data-testid="calendar-prev-month"
              >
                <CaretLeft size={16} weight="bold" />
              </Button>
              <Button
                onClick={goToToday}
                variant="ghost"
                size="sm"
                className="text-white/60 hover:text-white hover:bg-white/5 px-2 font-mono text-xs h-7"
                data-testid="calendar-today"
              >
                {format(calendarMonth, "MMMM yyyy")}
              </Button>
              <Button
                onClick={goToNextMonth}
                variant="ghost"
                size="sm"
                className="text-white/60 hover:text-white hover:bg-white/5 p-1.5 h-7 w-7"
                data-testid="calendar-next-month"
              >
                <CaretRight size={16} weight="bold" />
              </Button>
            </div>
          </div>
          
          <div className="p-3">
            {/* Day Labels + Weekly Total Header */}
            <div className="flex items-center gap-1 mb-1">
              <div className="flex-1 grid grid-cols-7 gap-0.5">
                {DAY_LABELS.map((day) => (
                  <div 
                    key={day} 
                    className="text-center text-[10px] font-mono text-white/40 py-1"
                  >
                    {day.slice(0, 3)}
                  </div>
                ))}
              </div>
              <div 
                className="w-20 text-center text-[10px] font-mono text-white/40 py-1"
                title="Percentages are based on capital at the start of each period"
              >
                Week
              </div>
            </div>
            
            {/* Calendar Weeks with Weekly Totals */}
            <div className="space-y-0.5">
              {getCalendarWeeks().map((week, weekIndex) => {
                const weeklyPnL = getWeeklyPnL(week);
                const isWeekSelected = calendarFilter?.type === 'week' && 
                  calendarFilter.weekDays && 
                  format(calendarFilter.weekDays[0], 'yyyy-MM-dd') === format(week[0], 'yyyy-MM-dd');
                
                return (
                  <div key={weekIndex} className="flex items-stretch gap-1">
                    {/* Week Days */}
                    <div className="flex-1 grid grid-cols-7 gap-0.5">
                      {week.map((day, dayIndex) => {
                        const isCurrentMonth = isSameMonth(day, calendarMonth);
                        const isToday = isSameDay(day, new Date());
                        const dailyPnL = getDailyPnL(day);
                        const isDaySelected = calendarFilter?.type === 'day' && isSameDay(calendarFilter.date, day);
                        
                        return (
                          <div
                            key={dayIndex}
                            onClick={() => handleDayClick(day)}
                            className={`
                              min-h-[3.5rem] p-1 rounded-sm font-mono flex flex-col justify-center items-center
                              ${isCurrentMonth ? 'text-white' : 'text-white/20'}
                              ${isToday ? 'ring-1 ring-white/30' : ''}
                              ${isDaySelected 
                                ? 'bg-[#00E676]/20 ring-2 ring-[#00E676]' 
                                : isWeekSelected && isCurrentMonth
                                  ? 'bg-[#00B8D4]/10'
                                  : isToday 
                                    ? 'bg-white/10' 
                                    : 'bg-[#0A0A0A]'
                              }
                              hover:bg-white/10 transition-colors cursor-pointer
                            `}
                            data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                          >
                            <span className={`text-[10px] ${isDaySelected ? 'text-[#00E676]' : 'text-white/40'}`}>
                              {format(day, 'd')}
                            </span>
                            {dailyPnL !== null && isCurrentMonth && (
                              <div className="flex flex-col items-center leading-tight">
                                <span 
                                  className={`text-[10px] font-bold ${
                                    dailyPnL.usd >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                                  }`}
                                  data-testid={`calendar-pnl-${format(day, 'yyyy-MM-dd')}`}
                                >
                                  {dailyPnL.usd >= 0 ? '+' : ''}{dailyPnL.usd.toFixed(0)}
                                </span>
                                {dailyPnL.percent !== null && (
                                  <span 
                                    className={`text-[8px] opacity-70 ${
                                      dailyPnL.usd >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                                    }`}
                                  >
                                    ({dailyPnL.usd >= 0 ? '+' : ''}{dailyPnL.percent.toFixed(1)}%)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Weekly Total - Clickable */}
                    <div 
                      onClick={() => handleWeekClick(week)}
                      className={`
                        w-20 min-h-[3.5rem] rounded-sm flex flex-col justify-center items-center font-mono cursor-pointer
                        transition-colors
                        ${isWeekSelected 
                          ? 'bg-[#00B8D4]/20 ring-2 ring-[#00B8D4]'
                          : weeklyPnL !== null 
                            ? 'bg-[#1A1A1A] hover:bg-white/10' 
                            : 'bg-[#0A0A0A] hover:bg-white/5'
                        }
                      `}
                      data-testid={`calendar-week-${weekIndex}`}
                    >
                      {weeklyPnL !== null && (
                        <div className="flex flex-col items-center leading-tight">
                          <span 
                            className={`text-[10px] font-bold ${
                              weeklyPnL.usd >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                            }`}
                          >
                            {weeklyPnL.usd >= 0 ? '+' : ''}{weeklyPnL.usd.toFixed(0)}
                          </span>
                          {weeklyPnL.percent !== null && (
                            <span 
                              className={`text-[8px] opacity-70 ${
                                weeklyPnL.usd >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                              }`}
                            >
                              ({weeklyPnL.usd >= 0 ? '+' : ''}{weeklyPnL.percent.toFixed(1)}%)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Notes Drawer - same pattern as OpenPositions */}
      {notesDrawerTrade && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 z-40 transition-opacity"
            onClick={closeNotesDrawer}
          />
          
          {/* Drawer */}
          <div 
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#121212] border-l border-white/10 z-50 shadow-2xl overflow-y-auto transition-transform"
            data-testid="notes-drawer"
          >
            {/* Drawer Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black font-heading">{notesDrawerTrade.asset}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    notesDrawerTrade.direction === "long" 
                      ? "bg-[#00E676]/10 text-[#00E676]" 
                      : "bg-[#FF3B30]/10 text-[#FF3B30]"
                  }`}>
                    {notesDrawerTrade.direction?.toUpperCase()}
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    notesDrawerTrade.outcome === 'win'
                      ? "bg-[#00E676]/10 text-[#00E676]"
                      : notesDrawerTrade.outcome === 'loss'
                      ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                      : "bg-white/10 text-white/60"
                  }`}>
                    {notesDrawerTrade.outcome?.toUpperCase() || notesDrawerTrade.status?.toUpperCase()}
                  </span>
                </div>
              </div>
              <button
                onClick={closeNotesDrawer}
                className="p-2 hover:bg-white/5 rounded-sm transition-colors"
                data-testid="close-notes-drawer-btn"
              >
                <X size={20} className="text-white/60" />
              </button>
            </div>

            {/* Trade Info Summary */}
            <div className="p-4 border-b border-white/10">
              <h4 className="text-white/40 text-xs font-mono mb-3">TRADE INFO</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-white/40 text-xs">Entry</span>
                  <p className="text-white font-mono">${notesDrawerTrade.entry?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-white/40 text-xs">RR</span>
                  <p className="text-white font-mono font-bold">{notesDrawerTrade.rr?.toFixed(2)}</p>
                </div>
                {notesDrawerTrade.profit_usd !== null && notesDrawerTrade.profit_usd !== undefined && (
                  <div>
                    <span className="text-white/40 text-xs">Profit</span>
                    {isSharedProfileView ? (
                      <p className={`font-mono font-bold ${
                        notesDrawerTrade.rr > 0 ? 'text-[#00E676]' : 
                        notesDrawerTrade.rr < 0 ? 'text-[#FF3B30]' : 'text-white'
                      }`}>
                        {notesDrawerTrade.rr > 0 ? '+' : ''}{(notesDrawerTrade.rr || 0).toFixed(2)}R
                      </p>
                    ) : (
                      <p className={`font-mono font-bold ${
                        notesDrawerTrade.profit_usd > 0 ? 'text-[#00E676]' : 
                        notesDrawerTrade.profit_usd < 0 ? 'text-[#FF3B30]' : 'text-white'
                      }`}>
                        {notesDrawerTrade.profit_usd > 0 ? '+' : ''}${notesDrawerTrade.profit_usd?.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <span className="text-white/40 text-xs">Date</span>
                  <p className="text-white/70 text-sm">
                    {notesDrawerTrade.created_at && format(new Date(notesDrawerTrade.created_at), "MMM d, yyyy")}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="p-4">
              <h4 className="text-white/40 text-xs font-mono mb-3 flex items-center gap-2">
                <Note size={14} />
                NOTES
                {isSharedProfileView && (
                  <span className="text-white/30 text-[10px] flex items-center gap-1">
                    <Lock size={10} /> View only
                  </span>
                )}
              </h4>
              <Textarea
                value={drawerNote}
                onChange={(e) => !isSharedProfileView && setDrawerNote(e.target.value)}
                placeholder={isSharedProfileView ? "Notes are view-only" : "Write your thoughts about this trade..."}
                className={`bg-[#0A0A0A] border-white/10 text-white min-h-[150px] resize-none focus:border-white/20 ${isSharedProfileView ? 'opacity-70 cursor-not-allowed' : ''}`}
                data-testid="notes-textarea"
                readOnly={isSharedProfileView}
              />
              {!isSharedProfileView && (
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={handleSaveNote}
                    disabled={savingNote || drawerNote === (notesDrawerTrade.notes || "")}
                    className="bg-white text-black font-bold hover:bg-gray-200"
                    data-testid="save-notes-btn"
                  >
                    {savingNote ? (
                      <>
                        <ArrowsClockwise size={16} className="animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check size={16} className="mr-2" />
                        Save Notes
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={!!cancelId} onOpenChange={(open) => {
        if (!open) {
          setCancelId(null);
          setCancelReason('');
        }
      }}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Mark Trade as Not Triggered</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This trade will be tracked in analytics as a planned trade that was not executed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Cancel Reason Selection */}
          <div className="py-4">
            <label className="text-white/60 text-sm mb-3 block">
              Why are you canceling this trade? <span className="text-[#FF3B30]">*</span>
            </label>
            <div className="space-y-2">
              {cancelReasonOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                    cancelReason === option.value 
                      ? 'border-[#FFA726] bg-[#FFA726]/10' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={option.value}
                    checked={cancelReason === option.value}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    cancelReason === option.value 
                      ? 'border-[#FFA726]' 
                      : 'border-white/30'
                  }`}>
                    {cancelReason === option.value && (
                      <div className="w-2 h-2 rounded-full bg-[#FFA726]" />
                    )}
                  </div>
                  <span className={`text-sm ${cancelReason === option.value ? 'text-white' : 'text-white/70'}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-cancel-dialog"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              onClick={() => setCancelReason('')}
            >
              No, Keep Trade
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-cancel"
              onClick={() => handleCancel(cancelId, cancelReason)}
              disabled={!cancelReason}
              className={`font-bold ${
                cancelReason 
                  ? 'bg-[#FFA726] text-black hover:bg-[#FFA726]/90' 
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
            >
              Yes, Mark as Not Triggered
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen Trade Confirmation Dialog */}
      <AlertDialog open={!!reopenId} onOpenChange={(open) => {
        if (!open) setReopenId(null);
      }}>
        <AlertDialogContent className="bg-[#121212] border border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Reopen this trade?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will return the trade to Open Positions and remove its closing data. The trade itself will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-reopen-dialog"
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-reopen-trade"
              onClick={() => handleReopen(reopenId)}
              disabled={reopeningId === reopenId}
              className="font-bold bg-[#00E676] text-black hover:bg-[#00E676]/90"
            >
              {reopeningId === reopenId ? "Reopening..." : "Reopen Trade"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Footer Branding */}
      <div className="flex items-center justify-center gap-2 mt-16 mb-6 text-sm text-white/40">
        <img
          src={LOGO_PUREBRAIN}
          alt="GENI"
          className="h-5 w-auto object-contain opacity-80"
        />
        <span>
          Powered by <span className="text-white font-medium">GENI</span>
        </span>
      </div>
    </div>
  );
}